import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useTranslation from '../../hooks/useTranslation';
import { formatDate as formatLocaleDate } from '../../utils/formatDate';

// Les formats courts d'en-tete (mois seul, jour+mois) n'existent pas dans le
// helper central : ils gardent toLocaleDateString, mais avec la langue de l'app.
const localeOf = (language) => (language === 'fr' ? 'fr-CA' : 'en-CA');
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { createTask, updateTask, listTasksByProject, listDependenciesByProject, createDependency, batchUpdateSortOrder } from '../../services/scheduleService';
import subcontractService from '../../services/subcontractService';
import { projectService } from '../../services/projectService';
import { statusVariant } from '../../utils/statusVariant';
import Badge from '../../components/ui/Badge';

// ─── Helpers ────────────────────────────────────────────────────────────────

// ─── Zoom configuration ───────────────────────────────────────────────────
const ZOOM_LEVELS = {
  day:   { dayWidth: 36, label: 'day' },
  week:  { dayWidth: 14, label: 'week' },
  month: { dayWidth: 5,  label: 'month' },
};
const ZOOM_ORDER = ['month', 'week', 'day'];

// Le helper central : il suit la langue de l'app et porte la garde T12:00:00
// qui empeche une date nue de reculer d'un jour au Quebec.
const formatDate = (dateStr, language) => formatLocaleDate(dateStr, language);

const formatDateShort = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d?.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const toISO = (date) => {
  const y = date?.getFullYear();
  const m = String(date?.getMonth() + 1)?.padStart(2, '0');
  const d = String(date?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDays = (dateStr, days) => {
  const d = new Date(dateStr + 'T00:00:00');
  d?.setDate(d?.getDate() + days);
  return toISO(d);
};

// Ecart en jours entre deux dates : sert aux POSITIONS (decalage depuis le debut
// de la ligne de temps, glisser-deposer). Ne pas l'utiliser pour une duree.
const diffDays = (a, b) => {
  const da = new Date(a + 'T00:00:00');
  const db = new Date(b + 'T00:00:00');
  return Math.round((db - da) / 86400000);
};

// Duree INCLUSIVE : debut et fin comptent (du 05 au 13 = 9 j ; 1 jour = 1 j).
// Meme regle que le trigger calculate_task_duration et que la vue Semaine.
const durationDays = (start, end) => diffDays(start, end) + 1;

// Fin d'une tache de N jours qui commence a `start` (inclusive).
const endForDuration = (start, days) => addDays(start, Math.max(1, days) - 1);

const buildDays = (startDate, count) => {
  const days = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(startDate.getTime());
    d?.setDate(d?.getDate() + i);
    days?.push(d);
  }
  return days;
};

const TASK_COLORS = [
  { bg: 'bg-orange-400', border: 'border-orange-500', progress: 'bg-orange-600', hex: '#fb923c', light: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-400' },
  { bg: 'bg-blue-400', border: 'border-blue-500', progress: 'bg-blue-600', hex: '#60a5fa', light: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-400' },
  { bg: 'bg-emerald-400', border: 'border-emerald-500', progress: 'bg-emerald-600', hex: '#34d399', light: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-400' },
  { bg: 'bg-violet-400', border: 'border-violet-500', progress: 'bg-violet-600', hex: '#a78bfa', light: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-400' },
  { bg: 'bg-amber-400', border: 'border-amber-500', progress: 'bg-amber-600', hex: '#fbbf24', light: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
  { bg: 'bg-rose-400', border: 'border-rose-500', progress: 'bg-rose-600', hex: '#fb7185', light: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-400' },
  { bg: 'bg-cyan-400', border: 'border-cyan-500', progress: 'bg-cyan-600', hex: '#22d3ee', light: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-400' },
];

const getColor = (index) => TASK_COLORS?.[index % TASK_COLORS?.length];

const STATUS_OPTIONS = ['Open', 'In progress', 'Done'];

const STATUS_STYLES = {
  'Open': 'bg-gray-100 text-gray-600',
  'In progress': 'bg-blue-50 text-blue-600',
  'Done': 'bg-emerald-50 text-emerald-600',
};

// Get Monday of the week containing a given date
const getMondayOfWeek = (date) => {
  const d = new Date(date);
  const day = d?.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d?.setDate(d?.getDate() + diff);
  d?.setHours(0, 0, 0, 0);
  return d;
};

const buildWeekDays = (monday) => {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d?.setDate(d?.getDate() + i);
    return d;
  });
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── Default column widths ───────────────────────────────────────────────────

const DEFAULT_COL_WIDTHS = {
  name: 280,
  assigned: 180,
  status: 130,
  start: 130,
  end: 130,
  duration: 80,
};

// ─── Tree Utilities ──────────────────────────────────────────────────────────

/**
 * Build a tree structure from a flat list using parent_id.
 * Returns root nodes with a `children` array each.
 */
const buildTree = (flatTasks) => {
  const map = {};
  flatTasks?.forEach((t) => { map[t?.id] = { ...t, children: [] }; });

  const roots = [];
  flatTasks?.forEach((t) => {
    if (t?.parent_id && map?.[t?.parent_id]) {
      map?.[t?.parent_id]?.children?.push(map?.[t?.id]);
    } else {
      roots?.push(map?.[t?.id]);
    }
  });
  // Chaque groupe (racine, enfants d'un parent) dans l'ordre choisi (sort_order).
  const sortByOrder = (a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0);
  const sortChildren = (nodes) => {
    nodes?.sort(sortByOrder);
    nodes?.forEach((n) => { if (n?.children?.length > 0) sortChildren(n?.children); });
  };
  sortChildren(roots);
  return roots;
};

/**
 * Flatten a tree into an ordered list respecting expand/collapse state.
 * Each item gets `level` (0-based depth) and `outlineNumber` (e.g. "1.2.3").
 * Also gets `hasChildren` boolean.
 */
const flattenTree = (nodes, expandedIds, level = 0, parentNumber = '') => {
  const result = [];
  nodes?.forEach((node, idx) => {
    const outlineNumber = parentNumber ? `${parentNumber}.${idx + 1}` : `${idx + 1}`;
    const hasChildren = node?.children?.length > 0;
    result?.push({ ...node, level, outlineNumber, hasChildren });
    if (hasChildren && expandedIds?.has(node?.id)) {
      const childRows = flattenTree(node?.children, expandedIds, level + 1, outlineNumber);
      result?.push(...childRows);
    }
  });
  return result;
};

// ─── WeekView Component ──────────────────────────────────────────────────────

const WeekView = ({ tasks, subcontractors, taskColorMap, onEditTask }) => {
  const { t, language } = useTranslation();
  const locale = localeOf(language);
  const [weekStart, setWeekStart] = useState(() => getMondayOfWeek(new Date()));

  const weekDays = buildWeekDays(weekStart);
  const weekDayISOs = weekDays?.map(toISO);

  const prevWeek = () => {
    const d = new Date(weekStart);
    d?.setDate(d?.getDate() - 7);
    setWeekStart(d);
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d?.setDate(d?.getDate() + 7);
    setWeekStart(d);
  };

  const goToday = () => setWeekStart(getMondayOfWeek(new Date()));

  const weekLabel = (() => {
    const start = weekDays?.[0];
    const end = weekDays?.[6];
    const opts = { month: 'short', day: 'numeric' };
    if (start?.getFullYear() !== end?.getFullYear()) {
      return `${start?.toLocaleDateString(locale, { ...opts, year: 'numeric' })} – ${end?.toLocaleDateString(locale, { ...opts, year: 'numeric' })}`;
    }
    return `${start?.toLocaleDateString(locale, opts)} – ${end?.toLocaleDateString(locale, { ...opts, year: 'numeric' })}`;
  })();

  const todayISO = toISO(new Date());

  const getSubName = (subId) => {
    if (!subId) return null;
    return subcontractors?.find((s) => s?.id === subId)?.name || null;
  };

  const getTasksForDay = (dayISO) => {
    return tasks?.filter((t) => {
      if (!t?.start_date || !t?.end_date) return false;
      return t?.start_date <= dayISO && t?.end_date >= dayISO;
    });
  };

  const groupBySubcontractor = (dayTasks) => {
    const groups = {};
    dayTasks?.forEach((task) => {
      const subName = getSubName(task?.subcontractor_id) || t('schedule.week.noSubcontractor');
      if (!groups?.[subName]) groups[subName] = [];
      groups?.[subName]?.push(task);
    });
    return groups;
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <button onClick={prevWeek} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors" title={t('schedule.week.previous')}>
            <Icon name="ChevronLeft" size={16} />
          </button>
          <button onClick={nextWeek} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors" title={t('schedule.week.next')}>
            <Icon name="ChevronRight" size={16} />
          </button>
          <span className="text-sm font-semibold text-gray-800 ml-1">{weekLabel}</span>
        </div>
        <button onClick={goToday} className="text-xs font-medium text-orange-500 hover:text-orange-600 border border-orange-200 hover:border-orange-300 px-3 py-1.5 rounded-lg transition-colors">
          {t('projectSchedule.today')}
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid grid-cols-7 gap-3 h-full">
          {weekDays?.map((day, dayIdx) => {
            const dayISO = weekDayISOs?.[dayIdx];
            const isToday = dayISO === todayISO;
            const isWeekend = dayIdx >= 5;
            const dayTasks = getTasksForDay(dayISO);
            const grouped = groupBySubcontractor(dayTasks);
            const hasWork = dayTasks?.length > 0;

            return (
              <div key={dayISO} className={`flex flex-col rounded-xl border ${isToday ? 'border-orange-300 bg-orange-50/30' : 'border-gray-200 bg-white'} overflow-hidden`}>
                <div className={`px-3 py-2.5 border-b flex-shrink-0 ${isToday ? 'bg-primary' : isWeekend ? 'bg-gray-50' : 'bg-gray-50'} ${isToday ? 'border-orange-400' : 'border-gray-200'}`}>
                  <p className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-white' : isWeekend ? 'text-gray-400' : 'text-gray-500'}`}>{t('schedule.days')?.[dayIdx] || DAY_LABELS?.[dayIdx]}</p>
                  <p className={`text-lg font-bold leading-tight ${isToday ? 'text-white' : isWeekend ? 'text-gray-400' : 'text-gray-800'}`}>{day?.getDate()}</p>
                  <p className={`text-xs ${isToday ? 'text-orange-100' : 'text-gray-400'}`}>{day?.toLocaleDateString(locale, { month: 'short' })}</p>
                </div>
                <div className="flex-1 p-2 space-y-2 overflow-y-auto">
                  {!hasWork && (
                    <div className="flex items-center justify-center h-full min-h-[60px]">
                      <p className="text-xs text-gray-300 italic">{t('schedule.week.noWork')}</p>
                    </div>
                  )}
                  {Object.entries(grouped)?.map(([subName, subTasks]) => (
                    <div key={subName} className="space-y-1">
                      <p className="text-xs font-semibold text-gray-500 truncate px-1 pt-1">{subName}</p>
                      {subTasks?.map((task) => {
                        const colorIdx = tasks?.findIndex((t) => t?.id === task?.id);
                        const color = getColor(colorIdx >= 0 ? colorIdx : 0);
                        const isMultiDay = task?.start_date !== task?.end_date;
                        return (
                          <button key={task?.id} onClick={() => onEditTask(task)} className={`w-full text-left rounded-lg px-2.5 py-2 border ${color?.light} border-l-4 hover:shadow-sm transition-all group`} style={{ borderLeftColor: color?.hex }}>
                            <p className={`text-xs font-semibold truncate ${color?.text}`}>{task?.name}</p>
                            {isMultiDay && <p className="text-xs text-gray-400 mt-0.5 truncate">{formatDate(task?.start_date, language)} → {formatDate(task?.end_date, language)}</p>}
                            {task?.progress > 0 && (
                              <div className="mt-1.5 h-1 bg-gray-200 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${color?.bg} opacity-70`} style={{ width: `${Math.min(100, task?.progress)}%` }} />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── GanttBar Component ──────────────────────────────────────────────────────

const GanttBar = ({ task, taskIndex, timelineStart, colorScheme, onUpdate, rowHeight, onDotMouseDown, dayWidth }) => {
  const { t } = useTranslation();
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const barRef = useRef(null);

  const startOffset = diffDays(toISO(timelineStart), task?.start_date);
  const duration = task?.duration_days || durationDays(task?.start_date, task?.end_date) || 1;
  const left = startOffset * dayWidth;
  const width = Math.max(duration * dayWidth, dayWidth);
  const progressWidth = Math.min(100, task?.progress || 0);

  const handleDragMouseDown = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const startX = e?.clientX;
    const origStart = task?.start_date;
    const origEnd = task?.end_date;

    const onMove = (me) => {
      const dx = me?.clientX - startX;
      const daysDelta = Math.round(dx / dayWidth);
      if (daysDelta === 0) return;
      const newStart = addDays(origStart, daysDelta);
      const newEnd = addDays(origEnd, daysDelta);
      if (barRef?.current) {
        const newLeft = (diffDays(toISO(timelineStart), newStart)) * dayWidth;
        barRef.current.style.left = `${newLeft}px`;
      }
      dragRef.current = { newStart, newEnd };
    };

    const onUp = async () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (dragRef?.current) {
        const { newStart, newEnd } = dragRef?.current;
        dragRef.current = null;
        await onUpdate(task?.id, { start_date: newStart, end_date: newEnd });
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [task, timelineStart, onUpdate, dayWidth]);

  const handleResizeMouseDown = useCallback((e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const startX = e?.clientX;
    const origEnd = task?.end_date;

    const onMove = (me) => {
      const dx = me?.clientX - startX;
      const daysDelta = Math.round(dx / dayWidth);
      if (daysDelta === 0) return;
      const newEnd = addDays(origEnd, daysDelta);
      // Une tache d'un jour (fin = debut) est permise ; la fin ne passe jamais avant le debut.
      if (diffDays(task?.start_date, newEnd) < 0) return;
      if (barRef?.current) {
        const newDuration = durationDays(task?.start_date, newEnd);
        barRef.current.style.width = `${Math.max(newDuration * dayWidth, dayWidth)}px`;
      }
      resizeRef.current = { newEnd };
    };

    const onUp = async () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (resizeRef?.current) {
        const { newEnd } = resizeRef?.current;
        resizeRef.current = null;
        await onUpdate(task?.id, { end_date: newEnd });
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [task, onUpdate, dayWidth]);

  const dotY = rowHeight / 2;
  const dotLeft = left;
  const dotRight = left + width;

  return (
    <>
      <div
        ref={barRef}
        className={`absolute top-1/2 -translate-y-1/2 rounded-lg border ${colorScheme?.bg} ${colorScheme?.border} shadow-sm select-none cursor-grab active:cursor-grabbing transition-shadow hover:shadow-md`}
        style={{ left: `${left}px`, width: `${width}px`, height: `${rowHeight - 12}px`, minWidth: `${dayWidth}px` }}
        onMouseDown={handleDragMouseDown}
      >
        <div className={`absolute inset-y-0 left-0 rounded-lg opacity-30 ${colorScheme?.progress}`} style={{ width: `${progressWidth}%` }} />
        <div className="absolute inset-0 flex items-center px-2 overflow-hidden">
          <span className="text-white text-xs font-semibold truncate drop-shadow-sm">{task?.name}</span>
        </div>
        <div className="absolute right-0 top-0 bottom-0 w-3 cursor-col-resize flex items-center justify-center rounded-r-lg hover:bg-black/10" onMouseDown={handleResizeMouseDown}>
          <div className="w-0.5 h-4 bg-white/60 rounded-full" />
        </div>
      </div>
      <div
        className="absolute z-20 w-3 h-3 rounded-full bg-white border-2 cursor-crosshair hover:scale-125 transition-transform"
        style={{ left: `${dotLeft - 6}px`, top: `${dotY - 6}px`, borderColor: colorScheme?.hex, boxShadow: `0 0 0 2px ${colorScheme?.hex}33` }}
        onMouseDown={(e) => { e?.preventDefault(); e?.stopPropagation(); onDotMouseDown(e, task, 'start'); }}
        title={t('schedule.tooltips.dragToConnect')}
      />
      <div
        className="absolute z-20 w-3 h-3 rounded-full bg-white border-2 cursor-crosshair hover:scale-125 transition-transform"
        style={{ left: `${dotRight - 6}px`, top: `${dotY - 6}px`, borderColor: colorScheme?.hex, boxShadow: `0 0 0 2px ${colorScheme?.hex}33` }}
        onMouseDown={(e) => { e?.preventDefault(); e?.stopPropagation(); onDotMouseDown(e, task, 'end'); }}
        title={t('schedule.tooltips.dragToConnect')}
      />
    </>
  );
};

// ─── ConnectorLines SVG overlay ──────────────────────────────────────────────

const ConnectorLines = ({ tasks, dependencies, timelineStart, rowHeight, headerHeight, dayWidth }) => {
  if (!dependencies?.length) return null;

  const taskMap = {};
  tasks?.forEach((t, idx) => { taskMap[t?.id] = { task: t, idx }; });

  return (
    <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 5, overflow: 'visible' }} width="100%" height="100%">
      {dependencies?.map((dep) => {
        const fromEntry = taskMap?.[dep?.depends_on_task_id];
        const toEntry = taskMap?.[dep?.task_id];
        if (!fromEntry || !toEntry) return null;

        const { task: fromTask, idx: fromIdx } = fromEntry;
        const { task: toTask, idx: toIdx } = toEntry;

        const fromDuration = fromTask?.duration_days || durationDays(fromTask?.start_date, fromTask?.end_date) || 1;
        const fromOffset = diffDays(toISO(timelineStart), fromTask?.start_date);
        const fromX = (fromOffset + fromDuration) * dayWidth;

        const toOffset = diffDays(toISO(timelineStart), toTask?.start_date);
        const toX = toOffset * dayWidth;

        const fromY = headerHeight + fromIdx * rowHeight + rowHeight / 2;
        const toY = headerHeight + toIdx * rowHeight + rowHeight / 2;

        const midX = fromX + (toX - fromX) / 2;
        const pathD = `M ${fromX} ${fromY} L ${midX} ${fromY} L ${midX} ${toY} L ${toX} ${toY}`;

        return (
          <g key={dep?.id}>
            <path d={pathD} fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.8" />
            <polygon points={`${toX},${toY} ${toX - 6},${toY - 4} ${toX - 6},${toY + 4}`} fill="#94a3b8" opacity="0.8" />
          </g>
        );
      })}
    </svg>
  );
};

// ─── DragLine SVG ─────────────────────────────────────────────────────────────

const DragLine = ({ from, to }) => {
  if (!from || !to) return null;
  return (
    <svg className="fixed inset-0 pointer-events-none" style={{ zIndex: 9999 }} width="100vw" height="100vh">
      <line x1={from.x} y1={from.y} x2={to?.x} y2={to?.y} stroke="#f97316" strokeWidth="2" strokeDasharray="6 3" opacity="0.9" />
      <circle cx={from.x} cy={from.y} r="4" fill="#f97316" />
      <circle cx={to?.x} cy={to?.y} r="4" fill="#f97316" opacity="0.6" />
    </svg>
  );
};

// ─── Inline Cell Components ──────────────────────────────────────────────────

const EditableCell = ({ value, onSave, type = 'text', className = '' }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setLocalVal(value); }, [value]);

  const commit = () => {
    setEditing(false);
    if (localVal !== value) onSave(localVal);
  };

  // Un clic SELECTIONNE la cellule (focus visible) ; double-clic ou Entree pour
  // modifier. Echap ou sortie du champ referment l'edition.
  const startEdit = () => { setEditing(true); setTimeout(() => inputRef?.current?.focus(), 0); };
  if (!editing) {
    return (
      <div
        tabIndex={0}
        className={`cursor-default hover:bg-gray-50 rounded px-1 py-0.5 truncate outline-none focus:bg-blue-50/30 focus:ring-1 focus:ring-blue-300/40 ${className}`}
        onDoubleClick={startEdit}
        onKeyDown={(e) => { if (e?.key === 'Enter') { e?.preventDefault(); startEdit(); } }}
        title={value ? `${value} — ${t('schedule.tooltips.clickToEdit')}` : t('schedule.tooltips.clickToEdit')}
      >
        {value || <span className="text-gray-300 italic text-xs">—</span>}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type={type}
      value={localVal || ''}
      onChange={(e) => setLocalVal(e?.target?.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e?.key === 'Enter') commit(); if (e?.key === 'Escape') { setEditing(false); setLocalVal(value); } }}
      className={`w-full border border-orange-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white ${className}`}
    />
  );
};

const StatusCell = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    if (editing) setTimeout(() => selectRef?.current?.focus(), 0);
  }, [editing]);

  const statusStyle = STATUS_STYLES?.[value] || 'bg-gray-100 text-gray-600';

  if (!editing) {
    return (
      <div className="cursor-pointer" onClick={() => setEditing(true)}>
        <Badge variant={statusVariant('schedule', value || 'Open')}>
          {value || 'Open'}
        </Badge>
      </div>
    );
  }

  return (
    <select
      ref={selectRef}
      value={value || 'Open'}
      onChange={(e) => { onSave(e?.target?.value); setEditing(false); }}
      onBlur={() => setEditing(false)}
      className="w-full border border-orange-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
    >
      {STATUS_OPTIONS?.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
};

const DurationCell = ({ task, onSave }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState('');
  const inputRef = useRef(null);

  const computedDuration = task?.duration_days || (task?.start_date && task?.end_date ? durationDays(task?.start_date, task?.end_date) : null);

  useEffect(() => { setLocalVal(String(computedDuration || '')); }, [computedDuration]);

  const commit = () => {
    setEditing(false);
    const days = parseInt(localVal, 10);
    if (!isNaN(days) && days > 0 && task?.start_date) {
      const newEnd = endForDuration(task?.start_date, days);
      onSave({ end_date: newEnd, duration_days: days });
    }
  };

  if (!editing) {
    return (
      <div
        tabIndex={0}
        className="cursor-default hover:bg-gray-50 rounded px-1 py-0.5 text-center outline-none focus:bg-blue-50/30 focus:ring-1 focus:ring-blue-300/40"
        onDoubleClick={() => { setEditing(true); setTimeout(() => inputRef?.current?.focus(), 0); }}
        onKeyDown={(e) => { if (e?.key === 'Enter') { e?.preventDefault(); setEditing(true); setTimeout(() => inputRef?.current?.focus(), 0); } }}
        title={t('schedule.tooltips.clickToEditDuration')}
      >
        {!computedDuration || computedDuration < 1
          ? <span className="text-gray-300 italic text-xs">—</span>
          : <span className="text-xs text-gray-700">{computedDuration}d</span>}
      </div>
    );
  }

  return (
    <input
      ref={inputRef}
      type="number"
      min="1"
      value={localVal}
      onChange={(e) => setLocalVal(e?.target?.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e?.key === 'Enter') commit(); if (e?.key === 'Escape') { setEditing(false); } }}
      className="w-full border border-orange-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white text-center"
    />
  );
};

// ─── Task Table Component (with resizable columns + hierarchy) ───────────────

const TaskTable = ({ flattenedTasks, subcontractors, rowHeight, headerHeight, onUpdateTask, colWidths, onColResize, expandedIds, onToggleExpand, onAddSubtask, onMoveTask, firstSiblingIds, lastSiblingIds, scrollRef }) => {
  const { t } = useTranslation();
  const getSubName = (subId) => {
    if (!subId) return '—';
    return subcontractors?.find((s) => s?.id === subId)?.name || '—';
  };

  const COLS = [
    { key: 'name', label: t('schedule.table.task') },
    { key: 'assigned', label: t('schedule.table.assigned') },
    { key: 'status', label: t('schedule.table.status') },
    { key: 'start', label: t('schedule.table.start') },
    { key: 'end', label: t('schedule.table.end') },
    { key: 'duration', label: t('schedule.table.duration') },
  ];

  // + la colonne etroite de reordonnement (▲/▼), avant le nom.
  const REORDER_W = 32;
  const totalWidth = REORDER_W + COLS?.reduce((sum, c) => sum + (colWidths?.[c?.key] || DEFAULT_COL_WIDTHS?.[c?.key]), 0);

  const handleColResizeMouseDown = useCallback((e, colKey) => {
    e?.preventDefault();
    e?.stopPropagation();
    const startX = e?.clientX;
    const startWidth = colWidths?.[colKey] || DEFAULT_COL_WIDTHS?.[colKey];

    const onMove = (me) => {
      const dx = me?.clientX - startX;
      const newWidth = Math.max(60, startWidth + dx);
      onColResize(colKey, newWidth);
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [colWidths, onColResize]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Table Header */}
      <div
        className="flex-shrink-0 bg-gray-50 border-b border-gray-200 flex text-xs font-semibold text-gray-500 uppercase tracking-wide"
        style={{ height: headerHeight, minWidth: totalWidth }}
      >
        <div className="flex-shrink-0" style={{ width: REORDER_W }} />
        {COLS?.map((col) => {
          const w = colWidths?.[col?.key] || DEFAULT_COL_WIDTHS?.[col?.key];
          return (
            <div
              key={col?.key}
              className="relative flex items-end pb-2 px-2 flex-shrink-0 select-none"
              style={{ width: w }}
            >
              <span className="truncate">{col?.label}</span>
              {/* Column resize handle */}
              <div
                className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-orange-300 transition-colors group"
                style={{ zIndex: 10 }}
                onMouseDown={(e) => handleColResizeMouseDown(e, col?.key)}
                title={t('schedule.tooltips.dragToResizeColumn')}
              >
                <div className="absolute inset-y-2 right-0 w-px bg-gray-200 group-hover:bg-orange-400 transition-colors" />
              </div>
            </div>
          );
        })}
      </div>
      {/* Table Rows */}
      <div ref={scrollRef} className="overflow-y-auto overflow-x-auto flex-1">
        <div style={{ minWidth: totalWidth }}>
          {flattenedTasks?.map((task, idx) => {
            const color = getColor(idx);
            const wName = colWidths?.name || DEFAULT_COL_WIDTHS?.name;
            const wAssigned = colWidths?.assigned || DEFAULT_COL_WIDTHS?.assigned;
            const wStatus = colWidths?.status || DEFAULT_COL_WIDTHS?.status;
            const wStart = colWidths?.start || DEFAULT_COL_WIDTHS?.start;
            const wEnd = colWidths?.end || DEFAULT_COL_WIDTHS?.end;
            const wDuration = colWidths?.duration || DEFAULT_COL_WIDTHS?.duration;

            const indentPx = (task?.level || 0) * 16;
            const isExpanded = expandedIds?.has(task?.id);

            return (
              <div
                key={task?.id}
                className="group flex border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
                style={{ height: rowHeight, alignItems: 'center' }}
              >
                {/* Reordonnement — monter / descendre parmi les taches du meme parent */}
                <div className="flex-shrink-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" style={{ width: REORDER_W }}>
                  <button
                    type="button"
                    onClick={(e) => { e?.stopPropagation(); onMoveTask?.(task?.id, 'up'); }}
                    className="p-0.5 text-gray-400 hover:text-gray-700 disabled:invisible"
                    disabled={firstSiblingIds?.has(task?.id)}
                    title={t('schedule.reorder.moveUp')}
                    aria-label={t('schedule.reorder.moveUp')}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 8L6 4L10 8" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e?.stopPropagation(); onMoveTask?.(task?.id, 'down'); }}
                    className="p-0.5 text-gray-400 hover:text-gray-700 disabled:invisible"
                    disabled={lastSiblingIds?.has(task?.id)}
                    title={t('schedule.reorder.moveDown')}
                    aria-label={t('schedule.reorder.moveDown')}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 4L6 8L10 4" />
                    </svg>
                  </button>
                </div>
                {/* Task Name */}
                <div className="flex items-center px-2 flex-shrink-0 min-w-0" style={{ width: wName }}>
                  {/* Indentation spacer */}
                  <div style={{ width: indentPx, flexShrink: 0 }} />
                  {/* Expand/collapse toggle */}
                  {task?.hasChildren ? (
                    <button
                      onClick={() => onToggleExpand(task?.id)}
                      className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-colors mr-1"
                      title={isExpanded ? t('schedule.tooltips.collapse') : t('schedule.tooltips.expand')}
                    >
                      <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={12} />
                    </button>
                  ) : (
                    <div className="flex-shrink-0 w-4 mr-1" />
                  )}
                  {/* Outline number */}
                  <span className="text-xs text-gray-400 font-mono mr-1.5 flex-shrink-0 select-none" style={{ minWidth: 28 }}>
                    {task?.outlineNumber}
                  </span>
                  {/* Color dot */}
                  <div className="w-2 h-2 rounded-full flex-shrink-0 mr-1.5" style={{ backgroundColor: color?.hex }} />
                  <EditableCell
                    value={task?.name}
                    onSave={(val) => onUpdateTask(task?.id, { name: val })}
                    className={`flex-1 min-w-0 ${task?.level === 0 ? 'text-sm font-semibold text-gray-900' : 'text-sm font-medium text-gray-700'}`}
                  />
                </div>

                {/* Assigned */}
                <div className="px-2 flex-shrink-0 min-w-0" style={{ width: wAssigned }}>
                  <span className="text-xs text-gray-600 block whitespace-normal leading-tight truncate" title={getSubName(task?.subcontractor_id)}>
                    {getSubName(task?.subcontractor_id)}
                  </span>
                </div>

                {/* Status */}
                <div className="px-2 flex-shrink-0" style={{ width: wStatus }}>
                  <StatusCell
                    value={task?.status || 'Open'}
                    onSave={(val) => onUpdateTask(task?.id, { status: val })}
                  />
                </div>

                {/* Start Date */}
                <div className="px-2 flex-shrink-0 min-w-0" style={{ width: wStart }}>
                  <EditableCell
                    value={task?.start_date || ''}
                    type="date"
                    onSave={(val) => {
                      if (val && task?.end_date && val <= task?.end_date) {
                        onUpdateTask(task?.id, { start_date: val });
                      } else if (val) {
                        onUpdateTask(task?.id, { start_date: val, end_date: val });
                      }
                    }}
                    className="text-xs text-gray-700"
                  />
                </div>

                {/* End Date */}
                <div className="px-2 flex-shrink-0 min-w-0" style={{ width: wEnd }}>
                  <EditableCell
                    value={task?.end_date || ''}
                    type="date"
                    onSave={(val) => {
                      if (val && task?.start_date && val >= task?.start_date) {
                        onUpdateTask(task?.id, { end_date: val });
                      }
                    }}
                    className="text-xs text-gray-700"
                  />
                </div>

                {/* Duration */}
                <div className="px-2 flex-shrink-0" style={{ width: wDuration }}>
                  <DurationCell
                    task={task}
                    onSave={(updates) => onUpdateTask(task?.id, updates)}
                  />
                </div>

                {/* Add Subtask action */}
                <div className="px-2 flex-shrink-0">
                  <button
                    onClick={() => onAddSubtask(task)}
                    className="flex items-center space-x-1 text-xs text-gray-400 hover:text-orange-500 transition-colors whitespace-nowrap"
                    title={t('schedule.addSubtask')}
                  >
                    <Icon name="Plus" size={12} />
                    <span>{t('schedule.addSubtask')}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Main SchedulePage ───────────────────────────────────────────────────────

const SchedulePage = () => {
  const { t, language } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [viewMode, setViewMode] = useState('gantt');

  // ── Expand/collapse state ────────────────────────────────────────────────
  const [expandedTaskIds, setExpandedTaskIds] = useState(new Set());

  const handleToggleExpand = useCallback((taskId) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      if (next?.has(taskId)) {
        next?.delete(taskId);
      } else {
        next?.add(taskId);
      }
      return next;
    });
  }, []);

  // ── Build tree + flattened visible list ──────────────────────────────────
  const flattenedTasks = useMemo(() => {
    const tree = buildTree(tasks);
    return flattenTree(tree, expandedTaskIds);
  }, [tasks, expandedTaskIds]);

  // ── Resizable panel state ────────────────────────────────────────────────
  const [leftPanelWidth, setLeftPanelWidth] = useState(55); // percentage
  const containerRef = useRef(null);
  const panelDragRef = useRef(null);

  // ── Resizable column widths ──────────────────────────────────────────────
  const [colWidths, setColWidths] = useState({ ...DEFAULT_COL_WIDTHS });

  const handleColResize = useCallback((colKey, newWidth) => {
    setColWidths((prev) => ({ ...prev, [colKey]: newWidth }));
  }, []);

  // ── Panel drag handle ────────────────────────────────────────────────────
  const handlePanelDragMouseDown = useCallback((e) => {
    e?.preventDefault();
    const container = containerRef?.current;
    if (!container) return;

    const containerRect = container?.getBoundingClientRect();
    const startX = e?.clientX;
    const startPct = leftPanelWidth;

    const onMove = (me) => {
      const dx = me?.clientX - startX;
      const containerWidth = containerRect?.width;
      const newPct = startPct + (dx / containerWidth) * 100;
      const minPct = (300 / containerWidth) * 100;
      const maxPct = 75;
      setLeftPanelWidth(Math.min(maxPct, Math.max(minPct, newPct)));
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [leftPanelWidth]);

  // Edit task modal state
  const [editTask, setEditTask] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  const [dragConn, setDragConn] = useState(null);
  const [dragPos, setDragPos] = useState(null);

  const [form, setForm] = useState({
    name: '',
    subcontractor_id: '',
    start_date: '',
    end_date: '',
    progress: '',
    parent_id: null,
    parent_name: null,
  });

  const [zoomLevel, setZoomLevel] = useState('day');
  const DAY_WIDTH = ZOOM_LEVELS[zoomLevel].dayWidth;

  // Ligne de temps : des dates des taches (14 j avant, 30 j apres, 90 j minimum) ;
  // sans tache datee, le 1er du mois courant sur 90 j.
  const { timelineStart: timelineStartDate, timelineDays } = useMemo(() => {
    const fallback = () => {
      const d = new Date();
      d.setDate(1);
      d.setHours(0, 0, 0, 0);
      return { timelineStart: d, timelineDays: 90 };
    };
    if (!tasks || tasks.length === 0) return fallback();
    const allDates = tasks.flatMap(t => [t.start_date, t.end_date].filter(Boolean));
    if (allDates.length === 0) return fallback();
    // 'T00:00:00' : une date nue serait lue en UTC et reculerait d'un jour au Quebec.
    const minDate = new Date(allDates.reduce((a, b) => a < b ? a : b) + 'T00:00:00');
    const maxDate = new Date(allDates.reduce((a, b) => a > b ? a : b) + 'T00:00:00');
    // Padding: 14 jours avant, 30 jours après
    minDate.setDate(minDate.getDate() - 14);
    maxDate.setDate(maxDate.getDate() + 30);
    const totalDays = Math.max(90, diffDays(toISO(minDate), toISO(maxDate)) + 1);
    return { timelineStart: minDate, timelineDays: totalDays };
  }, [tasks]);

  // Garder un ref pour que GanttBar et ConnectorLines puissent l'utiliser
  const timelineStart = useRef(timelineStartDate);
  timelineStart.current = timelineStartDate;

  const TIMELINE_DAYS = timelineDays;
  const days = buildDays(timelineStart.current, TIMELINE_DAYS);

  const ROW_HEIGHT = 52;
  const HEADER_HEIGHT = 72;

  useEffect(() => {
    if (id) fetchAll();
  }, [id]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [projectData, tasksResult, subcontractsData, depsResult] = await Promise.all([
        projectService?.getById(id),
        listTasksByProject(id),
        subcontractService?.getByProjectId(id),
        listDependenciesByProject(id),
      ]);
      setProject(projectData);
      setTasks(tasksResult?.data || []);
      setDependencies(depsResult?.data || []);

      const seen = new Set();
      const uniqueSubs = [];
      (subcontractsData || [])?.forEach((sc) => {
        if (sc?.subcontractor && !seen?.has(sc?.subcontractor?.id)) {
          seen?.add(sc?.subcontractor?.id);
          uniqueSubs?.push({
            id: sc?.subcontractor?.id,
            name: sc?.subcontractor?.companyName || sc?.subcontractor?.company_name || sc?.subcontractor?.name || '—',
          });
        }
      });
      setSubcontractors(uniqueSubs);
    } catch (err) {
      console.error('Error loading schedule data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (parentTask = null) => {
    setForm({
      name: '',
      subcontractor_id: '',
      start_date: '',
      end_date: '',
      progress: '',
      parent_id: parentTask ? parentTask?.id : null,
      parent_name: parentTask ? parentTask?.name : null,
    });
    setFormError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormError('');
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormError('');
    if (!form?.name?.trim()) { setFormError(t('schedule.errors.nameRequired')); return; }
    if (!form?.start_date) { setFormError(t('schedule.errors.startRequired')); return; }
    if (!form?.end_date) { setFormError(t('schedule.errors.endRequired')); return; }
    if (form?.end_date < form?.start_date) { setFormError(t('schedule.errors.endBeforeStart')); return; }

    setSubmitting(true);
    try {
      const payload = {
        project_id: id,
        name: form?.name?.trim(),
        subcontractor_id: form?.subcontractor_id || null,
        start_date: form?.start_date,
        end_date: form?.end_date,
        progress: form?.progress !== '' ? parseFloat(form?.progress) : 0,
        parent_id: form?.parent_id || null,
        // Nouvelle tache : en fin de son groupe (meme parent).
        sort_order: (tasks || [])
          ?.filter((t) => (t?.parent_id || null) === (form?.parent_id || null))
          ?.reduce((max, t) => Math.max(max, t?.sort_order ?? 0), 0) + 10,
      };
      const { error } = await createTask(payload);
      if (error) { setFormError(error?.message || t('schedule.errors.createFailed')); return; }
      // Auto-expand parent so new subtask is visible
      if (form?.parent_id) {
        setExpandedTaskIds((prev) => {
          const next = new Set(prev);
          next?.add(form?.parent_id);
          return next;
        });
      }
      handleCloseModal();
      await fetchAll();
    } catch (err) {
      setFormError(t('schedule.errors.unexpected'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTask = (task) => {
    setEditTask(task);
    setEditForm({
      name: task?.name || '',
      subcontractor_id: task?.subcontractor_id || '',
      start_date: task?.start_date || '',
      end_date: task?.end_date || '',
      progress: task?.progress !== undefined ? String(task?.progress) : '0',
    });
    setEditError('');
  };

  const handleCloseEditModal = () => {
    setEditTask(null);
    setEditError('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e?.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e?.preventDefault();
    setEditError('');
    if (!editForm?.name?.trim()) { setEditError(t('schedule.errors.nameRequired')); return; }
    if (!editForm?.start_date) { setEditError(t('schedule.errors.startRequired')); return; }
    if (!editForm?.end_date) { setEditError(t('schedule.errors.endRequired')); return; }
    if (editForm?.end_date < editForm?.start_date) { setEditError(t('schedule.errors.endBeforeStart')); return; }

    setEditSubmitting(true);
    try {
      const updates = {
        name: editForm?.name?.trim(),
        subcontractor_id: editForm?.subcontractor_id || null,
        start_date: editForm?.start_date,
        end_date: editForm?.end_date,
        progress: editForm?.progress !== '' ? parseFloat(editForm?.progress) : 0,
      };
      const { error } = await updateTask(editTask?.id, updates);
      if (error) { setEditError(error?.message || t('schedule.errors.updateFailed')); return; }
      handleCloseEditModal();
      await fetchAll();
    } catch (err) {
      setEditError(t('schedule.errors.unexpected'));
    } finally {
      setEditSubmitting(false);
    }
  };

  const cascadeShift = useCallback(async (movedTaskId, newEndDate, allTasks, allDeps) => {
    const directDependents = allDeps?.filter((d) => d?.depends_on_task_id === movedTaskId);
    if (!directDependents?.length) return { updatedTasks: allTasks, updatedDeps: allDeps };

    let currentTasks = [...allTasks];
    let currentDeps = [...allDeps];

    for (const dep of directDependents) {
      const depTask = currentTasks?.find((t) => t?.id === dep?.task_id);
      if (!depTask) continue;

      // Durees inclusives : le successeur commence le lendemain de la fin.
      if (depTask?.start_date <= newEndDate) {
        const duration = depTask?.duration_days || durationDays(depTask?.start_date, depTask?.end_date) || 1;
        const newStart = addDays(newEndDate, 1);
        const newEnd = endForDuration(newStart, duration);

        const { data } = await updateTask(depTask?.id, { start_date: newStart, end_date: newEnd });
        if (data) {
          currentTasks = currentTasks?.map((t) => (t?.id === depTask?.id ? { ...t, ...data } : t));
          const result = await cascadeShift(depTask?.id, newEnd, currentTasks, currentDeps);
          currentTasks = result?.updatedTasks;
          currentDeps = result?.updatedDeps;
        }
      }
    }

    return { updatedTasks: currentTasks, updatedDeps: currentDeps };
  }, []);

  // Premier / dernier de chaque groupe de freres : leur ▲ / ▼ est desactive.
  const { firstSiblingIds, lastSiblingIds } = useMemo(() => {
    const groups = {};
    (tasks || [])?.forEach((t) => {
      const key = t?.parent_id || '__root__';
      (groups[key] ||= [])?.push(t);
    });
    const firsts = new Set();
    const lasts = new Set();
    Object.values(groups)?.forEach((siblings) => {
      const sorted = [...siblings]?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0));
      if (sorted?.length > 0) {
        firsts?.add(sorted?.[0]?.id);
        lasts?.add(sorted?.[sorted?.length - 1]?.id);
      }
    });
    return { firstSiblingIds: firsts, lastSiblingIds: lasts };
  }, [tasks]);

  // Monter / descendre une tache parmi ses freres (meme parent_id). On echange
  // les deux places, puis on renumerote le groupe par pas de 10 : deux freres a
  // sort_order egal (ex. 0 par defaut) ne bloquent ainsi jamais l'echange.
  // Seules les lignes dont le rang change sont ecrites.
  const handleMoveTask = useCallback(async (taskId, direction) => {
    const task = tasks?.find((t) => t?.id === taskId);
    if (!task) return;
    const siblings = tasks
      ?.filter((t) => (t?.parent_id || null) === (task?.parent_id || null))
      ?.sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0));
    const idx = siblings?.findIndex((s) => s?.id === taskId);
    const neighborIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || neighborIdx < 0 || neighborIdx >= siblings?.length) return;

    const reordered = [...siblings];
    [reordered[idx], reordered[neighborIdx]] = [reordered[neighborIdx], reordered[idx]];
    const updates = reordered
      ?.map((t, i) => ({ id: t?.id, sort_order: (i + 1) * 10, previous: t?.sort_order }))
      ?.filter((u) => u?.sort_order !== u?.previous)
      ?.map(({ id: taskRowId, sort_order }) => ({ id: taskRowId, sort_order }));
    if (!updates?.length) return;

    // Mise a jour optimiste, puis persistance ; relecture complete si erreur.
    const newOrder = Object.fromEntries(updates?.map((u) => [u?.id, u?.sort_order]));
    setTasks((prev) => prev?.map((t) => (newOrder?.[t?.id] != null ? { ...t, sort_order: newOrder?.[t?.id] } : t)));
    const { error } = await batchUpdateSortOrder(updates);
    if (error) {
      console.error('Error reordering tasks:', error);
      await fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const handleUpdateTask = useCallback(async (taskId, updates) => {
    setTasks((prev) => prev?.map((t) => (t?.id === taskId ? { ...t, ...updates } : t)));

    const { data, error } = await updateTask(taskId, updates);
    if (!error && data) {
      setTasks((prev) => prev?.map((t) => (t?.id === taskId ? { ...t, ...data } : t)));

      if (updates?.end_date || updates?.start_date) {
        const movedTask = data;
        if (movedTask) {
          let currentTasks = await listTasksByProject(id);
          const { updatedTasks: shifted } = await cascadeShift(taskId, movedTask?.end_date, currentTasks?.data || [], dependencies);
          setTasks(shifted);
        }
      }
    } else if (error) {
      await fetchAll();
    }
  }, [dependencies, cascadeShift, id]);

  const handleDotMouseDown = useCallback((e, task, dotSide) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragConn({ fromTask: task, fromDot: dotSide, startX: e?.clientX, startY: e?.clientY });
    setDragPos({ x: e?.clientX, y: e?.clientY });
  }, []);

  useEffect(() => {
    if (!dragConn) return;

    const onMove = (e) => {
      setDragPos({ x: e?.clientX, y: e?.clientY });
    };

    const onUp = async (e) => {
      setDragConn(null);
      setDragPos(null);

      const el = document.elementFromPoint(e?.clientX, e?.clientY);
      const targetBarEl = el?.closest('[data-task-id]');
      if (!targetBarEl) return;

      const targetTaskId = targetBarEl?.getAttribute('data-task-id');
      if (!targetTaskId || targetTaskId === dragConn?.fromTask?.id) return;

      const { error } = await createDependency(targetTaskId, dragConn?.fromTask?.id);
      if (!error) {
        const { data: newDeps } = await listDependenciesByProject(id);
        setDependencies(newDeps || []);

        const targetTask = tasks?.find((t) => t?.id === targetTaskId);
        const fromTask = dragConn?.fromTask;
        if (targetTask && fromTask && targetTask?.start_date <= fromTask?.end_date) {
          const duration = targetTask?.duration_days || durationDays(targetTask?.start_date, targetTask?.end_date) || 1;
          const newStart = addDays(fromTask?.end_date, 1);
          const newEnd = endForDuration(newStart, duration);
          await handleUpdateTask(targetTaskId, { start_date: newStart, end_date: newEnd });
        }
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragConn, tasks, id, handleUpdateTask]);

  const monthGroups = (() => {
    const groups = [];
    let current = null;
    days?.forEach((d, i) => {
      const key = `${d?.getFullYear()}-${d?.getMonth()}`;
      if (!current || current?.key !== key) {
        current = { key, label: d?.toLocaleDateString(localeOf(language), { month: 'long', year: 'numeric' }), start: i, count: 1 };
        groups?.push(current);
      } else {
        current.count++;
      }
    });
    return groups;
  })();

  const today = toISO(new Date());

  // A l'ouverture du Gantt : la colonne d'aujourd'hui est centree dans la vue.
  const ganttScrollRef = useRef(null);
  useEffect(() => {
    if (viewMode !== 'gantt' || loading) return;
    const el = ganttScrollRef?.current;
    if (!el || !timelineStart?.current) return;
    const offset = diffDays(toISO(timelineStart?.current), today);
    if (offset < 0 || offset >= TIMELINE_DAYS) return;
    el.scrollLeft = Math.max(0, offset * DAY_WIDTH - el.clientWidth / 2 + DAY_WIDTH / 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, loading, zoomLevel]);

  const scrollToToday = () => {
    const el = ganttScrollRef?.current;
    if (!el || !timelineStart?.current) return;
    const offset = diffDays(toISO(timelineStart.current), today);
    if (offset >= 0 && offset < TIMELINE_DAYS) {
      el.scrollLeft = Math.max(0, offset * DAY_WIDTH - el.clientWidth / 2 + DAY_WIDTH / 2);
    }
  };

  // Zoom : la date au centre de la vue reste au centre.
  const handleZoomChange = useCallback((newLevel) => {
    const el = ganttScrollRef?.current;
    if (!el || !timelineStart?.current) {
      setZoomLevel(newLevel);
      return;
    }
    const oldDayWidth = ZOOM_LEVELS[zoomLevel].dayWidth;
    const centerX = el.scrollLeft + el.clientWidth / 2;
    const centerDayOffset = centerX / oldDayWidth;

    setZoomLevel(newLevel);

    // Après render, scroller pour garder cette date au centre
    requestAnimationFrame(() => {
      const newDayWidth = ZOOM_LEVELS[newLevel].dayWidth;
      el.scrollLeft = Math.max(0, centerDayOffset * newDayWidth - el.clientWidth / 2);
    });
  }, [zoomLevel]);

  // Defilement vertical synchronise : tableau (gauche) <-> Gantt (droite).
  const tableScrollRef = useRef(null);
  const isSyncing = useRef(false);
  const hasTasks = tasks?.length > 0;
  useEffect(() => {
    if (viewMode !== 'gantt' || loading) return;
    const left = tableScrollRef?.current;
    const right = ganttScrollRef?.current;
    if (!left || !right) return;

    const syncLeft = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      right.scrollTop = left.scrollTop;
      isSyncing.current = false;
    };
    const syncRight = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      left.scrollTop = right.scrollTop;
      isSyncing.current = false;
    };

    left.addEventListener('scroll', syncLeft);
    right.addEventListener('scroll', syncRight);
    return () => {
      left.removeEventListener('scroll', syncLeft);
      right.removeEventListener('scroll', syncRight);
    };
  }, [viewMode, loading, hasTasks]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Icon name="Loader2" size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Page Header */}
      <div className="flex items-start justify-between flex-shrink-0">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="CalendarDays" size={18} className="text-primary" />
            <h1 className="text-lg font-semibold text-[#2C3E50]">{t('schedule.title')}</h1>
          </div>
          {project && (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <span
                  onClick={() => navigate(`/project-detail`)}
                  className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
                >
                  {project?.projectNumber || ''}
                </span>
                <span className="text-sm text-primary font-normal">-</span>
                <span
                  onClick={() => navigate(`/project-detail`)}
                  className="text-sm text-primary font-normal hover:text-primary-dark cursor-pointer transition-colors"
                >
                  {project?.name || project?.project_name || ''}
                </span>
              </div>
              <p className="text-xs text-gray-500">{t('schedule.subtitle')}</p>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-3">
          {/* View Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('gantt')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'gantt' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Icon name="BarChart2" size={14} />
              <span>{t('schedule.viewGantt')}</span>
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${viewMode === 'week' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <Icon name="CalendarDays" size={14} />
              <span>{t('schedule.viewWeek')}</span>
            </button>
          </div>

          {viewMode === 'gantt' && (
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              {/* Bouton Aujourd'hui */}
              <button
                onClick={scrollToToday}
                className="px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-white transition-all"
                title={t('schedule.zoom.today')}
              >
                {t('schedule.zoom.today')}
              </button>

              <div className="w-px h-4 bg-gray-300" />

              {/* Boutons zoom */}
              {ZOOM_ORDER.map((level) => (
                <button
                  key={level}
                  onClick={() => handleZoomChange(level)}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
                    zoomLevel === level
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t(`schedule.zoom.${level}`)}
                </button>
              ))}
            </div>
          )}

          <button
            onClick={handleOpenModal}
            className="flex items-center space-x-2 bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Icon name="Plus" size={16} />
            <span>{t('schedule.addTask')}</span>
          </button>
        </div>
      </div>
      {/* Empty state */}
      {tasks?.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col items-center justify-center py-16 text-gray-400">
          <Icon name="CalendarDays" size={40} className="mb-3 text-gray-300" />
          <p className="text-base font-medium text-gray-500">{t('schedule.noTasks')}</p>
          <p className="text-sm mt-1">{t('schedule.noTasksHint')}</p>
        </div>
      ) : viewMode === 'week' ? (
        <WeekView
          tasks={tasks}
          subcontractors={subcontractors}
          taskColorMap={tasks?.reduce((acc, t, i) => { acc[t.id] = getColor(i); return acc; }, {})}
          onEditTask={handleEditTask}
        />
      ) : (
        /* ── Gantt view: resizable left + right panels ── */
        (<div
          ref={containerRef}
          className="flex bg-white rounded-xl border border-gray-200 overflow-hidden flex-1 min-h-0"
          style={{ userSelect: panelDragRef?.current ? 'none' : undefined }}
        >
          {/* LEFT — Task Table (resizable) */}
          <div
            className="flex-shrink-0 flex flex-col overflow-hidden"
            style={{ width: `${leftPanelWidth}%`, minWidth: 300 }}
          >
            <TaskTable
              flattenedTasks={flattenedTasks}
              subcontractors={subcontractors}
              rowHeight={ROW_HEIGHT}
              headerHeight={HEADER_HEIGHT}
              onUpdateTask={handleUpdateTask}
              colWidths={colWidths}
              onColResize={handleColResize}
              expandedIds={expandedTaskIds}
              onToggleExpand={handleToggleExpand}
              onAddSubtask={(task) => handleOpenModal(task)}
              onMoveTask={handleMoveTask}
              firstSiblingIds={firstSiblingIds}
              lastSiblingIds={lastSiblingIds}
              scrollRef={tableScrollRef}
            />
          </div>
          {/* PANEL DRAG HANDLE */}
          <div
            className="flex-shrink-0 relative flex items-center justify-center bg-gray-100 hover:bg-orange-100 transition-colors group"
            style={{ width: 5, cursor: 'col-resize', zIndex: 20 }}
            onMouseDown={handlePanelDragMouseDown}
            title={t('schedule.tooltips.dragToResizePanels')}
          >
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gray-200 group-hover:bg-orange-300 transition-colors" />
            {/* grip dots */}
            <div className="flex flex-col gap-1 z-10">
              {[0,1,2]?.map((i) => (
                <div key={i} className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-orange-400 transition-colors" />
              ))}
            </div>
          </div>
          {/* RIGHT — Gantt Timeline (flex-1) */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">
            <div ref={ganttScrollRef} className="overflow-x-auto overflow-y-auto flex-1">
              <div style={{ width: TIMELINE_DAYS * DAY_WIDTH, minWidth: '100%', position: 'relative' }}>
                {/* Month header */}
                <div className="flex bg-gray-50 border-b border-gray-200 sticky top-0 z-10" style={{ height: 36 }}>
                  {monthGroups?.map((mg) => (
                    <div key={mg?.key} className="flex-shrink-0 border-r border-gray-200 flex items-center px-2" style={{ width: mg?.count * DAY_WIDTH }}>
                      <span className="text-xs font-semibold text-gray-600 truncate">{mg?.label}</span>
                    </div>
                  ))}
                </div>

                {/* Day header */}
                <div className="flex bg-gray-50 border-b border-gray-200 sticky top-9 z-10" style={{ height: 36 }}>
                  {days?.map((d, i) => {
                    const isToday = toISO(d) === today;
                    const isWeekend = d?.getDay() === 0 || d?.getDay() === 6;
                    // Libelle selon le zoom : chaque jour / les lundis / le 1er et le 15.
                    let dayLabel = '';
                    if (zoomLevel === 'day') {
                      dayLabel = d?.getDate();
                    } else if (zoomLevel === 'week') {
                      if (d?.getDay() === 1) dayLabel = d?.getDate();
                    } else {
                      const dayNum = d?.getDate();
                      if (dayNum === 1 || dayNum === 15) dayLabel = dayNum;
                    }
                    return (
                      <div
                        key={i}
                        className={`flex-shrink-0 flex items-center justify-center border-r border-gray-100 text-xs ${isToday ? 'bg-orange-50 text-orange-600 font-bold' : isWeekend ? 'bg-gray-200/60 text-gray-400' : 'text-gray-500'}`}
                        style={{ width: DAY_WIDTH }}
                      >
                        {dayLabel}
                      </div>
                    );
                  })}
                </div>

                {/* Task rows + connector lines — use flattenedTasks for hierarchy sync */}
                <div style={{ position: 'relative' }}>
                  <ConnectorLines
                    tasks={flattenedTasks}
                    dependencies={dependencies}
                    timelineStart={timelineStart?.current}
                    rowHeight={ROW_HEIGHT}
                    headerHeight={0}
                    dayWidth={DAY_WIDTH}
                  />

                  {/* Today marker line */}
                  {(() => {
                    const todayOffset = diffDays(toISO(timelineStart.current), today);
                    if (todayOffset < 0 || todayOffset >= TIMELINE_DAYS) return null;
                    return (
                      <div
                        className="absolute top-0 bottom-0 pointer-events-none"
                        style={{
                          left: todayOffset * DAY_WIDTH + DAY_WIDTH / 2,
                          width: 2,
                          background: 'rgba(249, 115, 22, 0.6)',
                          zIndex: 6,
                        }}
                      />
                    );
                  })()}

                  {flattenedTasks?.map((task, idx) => {
                    const color = getColor(idx);
                    return (
                      <div
                        key={task?.id}
                        data-task-id={task?.id}
                        className="relative border-b border-gray-100 last:border-b-0"
                        style={{ height: ROW_HEIGHT, width: TIMELINE_DAYS * DAY_WIDTH }}
                      >
                        {/* Vertical grid lines */}
                        {days?.map((d, i) => {
                          const isToday = toISO(d) === today;
                          const isWeekend = d?.getDay() === 0 || d?.getDay() === 6;
                          return (
                            <div
                              key={i}
                              className={`absolute top-0 bottom-0 border-r ${isToday ? 'border-orange-300 bg-orange-50/40' : isWeekend ? 'border-gray-200 bg-gray-200/40' : 'border-gray-100'}`}
                              style={{ left: i * DAY_WIDTH, width: DAY_WIDTH }}
                            />
                          );
                        })}
                        {/* Task bar with dots */}
                        {task?.start_date && task?.end_date && (
                          <GanttBar
                            task={task}
                            taskIndex={idx}
                            timelineStart={timelineStart?.current}
                            colorScheme={color}
                            onUpdate={handleUpdateTask}
                            rowHeight={ROW_HEIGHT}
                            onDotMouseDown={handleDotMouseDown}
                            dayWidth={DAY_WIDTH}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>)
      )}
      {/* Live drag-to-connect line */}
      {dragConn && dragPos && (
        <DragLine
          from={{ x: dragConn?.startX, y: dragConn?.startY }}
          to={dragPos}
        />
      )}
      {/* Add Task Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{form?.parent_id ? t('schedule.modal.createSubtask') : t('schedule.modal.createTask')}</h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">{formError}</div>
              )}

              {form?.parent_id && (
                <div className="flex items-center space-x-2 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                  <Icon name="CornerDownRight" size={14} className="text-orange-500 flex-shrink-0" />
                  <span className="text-xs text-orange-700 font-medium">
                    {t('schedule.modal.subtaskOf')} {form?.parent_name || t('schedule.modal.parentTask')}
                  </span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.taskName')} <span className="text-red-500">*</span></label>
                <input type="text" name="name" value={form?.name} onChange={handleChange} placeholder={t('schedule.modal.taskNamePlaceholder')} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.subcontractor')}</label>
                <select name="subcontractor_id" value={form?.subcontractor_id} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                  <option value="">— None —</option>
                  {subcontractors?.map((s) => <option key={s?.id} value={s?.id}>{s?.name}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.startDate')} <span className="text-red-500">*</span></label>
                  <input type="date" name="start_date" value={form?.start_date} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.endDate')} <span className="text-red-500">*</span></label>
                  <input type="date" name="end_date" value={form?.end_date} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.progress')} <span className="text-gray-400 font-normal">— {t('schedule.modal.optional')}</span></label>
                <input type="number" name="progress" value={form?.progress} onChange={handleChange} min="0" max="100" step="1" placeholder="0" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">{t('schedule.modal.cancel')}</button>
                <button type="submit" disabled={submitting} className="flex items-center space-x-2 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-60">
                  {submitting && <Icon name="Loader2" size={14} className="animate-spin" />}
                  <span>{submitting ? t('schedule.modal.creating') : form?.parent_id ? t('schedule.modal.createSubtask') : t('schedule.modal.createTask')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Edit Task Modal */}
      {editTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">{t('schedule.modal.editTask')}</h2>
              <button onClick={handleCloseEditModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="px-6 py-5 space-y-4">
              {editError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">{editError}</div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.taskName')} <span className="text-red-500">*</span></label>
                <input type="text" name="name" value={editForm?.name} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.subcontractor')}</label>
                <select name="subcontractor_id" value={editForm?.subcontractor_id} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white">
                  <option value="">— None —</option>
                  {subcontractors?.map((s) => <option key={s?.id} value={s?.id}>{s?.name}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.startDate')} <span className="text-red-500">*</span></label>
                  <input type="date" name="start_date" value={editForm?.start_date} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.endDate')} <span className="text-red-500">*</span></label>
                  <input type="date" name="end_date" value={editForm?.end_date} onChange={handleEditChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.modal.progress')}</label>
                <input type="number" name="progress" value={editForm?.progress} onChange={handleEditChange} min="0" max="100" step="1" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={handleCloseEditModal} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">{t('schedule.modal.cancel')}</button>
                <button type="submit" disabled={editSubmitting} className="flex items-center space-x-2 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-60">
                  {editSubmitting && <Icon name="Loader2" size={14} className="animate-spin" />}
                  <span>{editSubmitting ? t('schedule.modal.saving') : t('schedule.modal.saveChanges')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulePage;
